import { useEffect, useMemo, useRef, useState, type FC } from "react";

import HebrewLocal from "@fullcalendar/core/locales/he";
import dayGridPlugin from "@fullcalendar/daygrid";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { AddCircleOutlineRounded as PlusIcon } from "@mui/icons-material";

import { type Group } from "@entities";
import type { CalendarApi, EventSourceInput } from "@fullcalendar/core";
import { Button, CircularProgress, Stack } from "@mui/material";
import { groupRepo } from "@shared/Repositories";
import clsx from "clsx";
import { endOfWeek, startOfWeek } from "date-fns";
import dayjs from "dayjs";
import {
  getGroups,
  processString,
} from "../../components/calender/calander.functions";
import { CustomToolbar } from "../../components/calender/custom-toolbar/CustomToolBar";
import GroupCard from "../../components/popups/group-card/GroupCard";
import { useCalendarGroups } from "../../queries/groups.query";
import "./Calender.scss";

export interface CalenderEvent {
  title: string;
  date?: Date; // For all-day events
  start?: Date; // For timed events
  end?: Date; // For timed events
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  allDay?: boolean;
  id: string;
  url?: string;
}

export const GroupsCalendar: FC = () => {
  const [groupId, setGroupId] = useState<string>("");
  const [group, setGroup] = useState<Group>();
  const [calendarApi, setCalendarApi] = useState<CalendarApi | null>(null);
  const calendarRef = useRef<FullCalendar>(null);
  const [tableHeight, setTableHeight] = useState<number | undefined>(undefined);
  const [startEndDates, setStartEndDates] = useState({ start: "", end: "" });
  const [windowSize, setWindowSize] = useState({ height: window.innerHeight });
  const [isAddGroup, setIsAddGroup] = useState<boolean>(false);
  const [selectedInfo, setSelectedInfo] = useState<string>("preferredTime");
  const calendarRangeStart = startEndDates.start
    ? new Date(startEndDates.start)
    : undefined;
  const calendarRangeEnd = startEndDates.end
    ? new Date(startEndDates.end)
    : undefined;
  const { data: groups = [], isPending, refetch } = useCalendarGroups({
    startDate: calendarRangeStart,
    endDate: calendarRangeEnd,
  });
  const events = useMemo<EventSourceInput>(
    () => getGroups(groups, selectedInfo === "groupMissions"),
    [groups, selectedInfo],
  );

  useEffect(() => {
    if (groupId) getGroupDetails();
  }, [groupId]);

  const getGroupDetails = async () => {
    const group = await groupRepo.findOne({
      where: { id: groupId },
      include: { admin: true, status: true },
    });
    setGroup(group);
  };

  useEffect(() => {
    function handleWindowResize() {
      setWindowSize({ height: window.innerHeight });
    }
    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  // Set start and end dates whenever calendar dates change
  const handleDatesSet = (dateInfo: { start: Date; end: Date; }) => {
    setStartEndDates({
      start: dayjs(dateInfo.start).format("YYYY-MM-DD"),
      end: dayjs(dateInfo.end).format("YYYY-MM-DD"),
    });
  };

  useEffect(() => {
    if (calendarRef.current) {
      setCalendarApi(calendarRef.current.getApi());
    }
  }, [calendarRef]);

  const tableElement = document.querySelector<HTMLTableElement>(
    ".fc-scroller-liquid-absolute",
  );

  useEffect(() => {
    findTableHeight();
    calendarReorder();
  }, [window.innerHeight, tableHeight, calendarRef, tableElement]); //Will not work! window.innerHeight is not a state, you must add an event listener of `resize` to the window in order to lister for changes

  function findTableHeight() {
    const currentTableHeight = tableElement?.getBoundingClientRect().height;
    setTableHeight(currentTableHeight);
  }

  function calendarReorder() {
    const calendarEl = calendarRef.current?.getApi();
    if (calendarEl && tableHeight) {
      const rows = (calendarEl as any).el.querySelectorAll(
        ".fc-daygrid-body tr",
      );
      const rowHeight = tableHeight / 5;
      rows.forEach((row: HTMLElement) => {
        row.style.height = `${rowHeight}px`;
      });
    }
  }

  const handleDateChange = (date: Date | null) => {
    if (date && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      let startOfWeekDate: Date;
      let endOfWeekDate: Date;
      switch (calendarApi.view.type) {
        case "timeGridWeek":
          startOfWeekDate = startOfWeek(date);
          endOfWeekDate = endOfWeek(date);
          calendarApi.gotoDate(startOfWeekDate);
          calendarApi.changeView("timeGridWeek", {
            start: startOfWeekDate,
            end: endOfWeekDate,
          });
          break;
        case "dayGridMonth":
          calendarApi.gotoDate(date);
          calendarApi.changeView("dayGridMonth");
          break;
        default:
          calendarApi.gotoDate(date);
          break;
      }
    }
    calendarReorder();
  };

  return (
    <div className="calender-container">
      <div className="calender-header">
        <div className="title">משימות</div>
        <Button
          sx={{
            gap: "0.5rem",
            borderRadius: "1rem",
            height: "fit-content !important",
            alignItems: "center",
            padding: "0.4rem 1.5rem",
            backgroundColor: "#05204CF2",
          }}
          variant="contained"
          onClick={() => setIsAddGroup(true)}
        >
          <PlusIcon sx={{ fontSize: "1.25rem" }} aria-hidden="true" />
          הוספת קבוצה
        </Button>
      </div>
      <div className="calender-toolbar">
        {calendarApi && (
          <CustomToolbar
            handlefilterSelect={(event) => {
              setSelectedInfo(event.target.value);
            }}
            calendarApi={calendarApi}
            handleDateChange={(date: Date | null) => {
              handleDateChange(date);
            }}
            changeMaxStack={() => { }}
            isGroup={true}
          />
        )}
      </div>
      {isPending ? (
        <Stack
          width="100%"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      ) : (
        <div
          className={clsx([
            "mission-and-calendar-container",
            // CalendarApi?.view.type === "timeGridDay" && "grid",
          ])}
        >
          <FullCalendar
            datesSet={handleDatesSet}
            dayMaxEvents={true}
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin]}
            headerToolbar={{ center: "", left: "", right: "" }}
            initialView="dayGridMonth"
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            slotDuration="01:00:00"
            slotLabelFormat={{
              hour: "2-digit",
              minute: "2-digit",
            }}
            dayHeaderContent={(arg) => (
              <>
                <div>
                  <div
                    className="header-cell-day"
                    style={{ width: "fit-content" }}
                  >
                    <p>{processString(arg.text).beforeNumber}</p>
                    {calendarApi?.view.type !== "dayGridMonth" && (
                      <p>{processString(arg.text).afterNumber}</p>
                    )}
                  </div>
                </div>
              </>
            )}
            allDaySlot={false}
            viewClassNames="main-slots"
            displayEventTime
            locale={HebrewLocal}
            eventClick={(args) => {
              if (selectedInfo !== "groupMissions") setGroupId(args.event.id);
            }}
            scrollTime="08:00:00"
            showNonCurrentDates={false}
            fixedWeekCount={false}
            events={events}
            footerToolbar={false}
            eventContent={(arg) => (
              <div
                className={arg.backgroundColor}
                style={{ paddingInline: "0.5rem" }}
              >
                <p
                  style={{ color: "white" }}
                  className={clsx("event-content", {
                    clickable: Boolean(arg.event.url),
                  })}
                >
                  {arg.event.title}
                </p>
              </div>
            )}
            eventClassNames={(arg) => {
              if (arg.event.allDay) {
                return clsx(["full-day-event", arg.backgroundColor]);
              } else {
                return clsx(["non-full-day", arg.backgroundColor]);
              }
            }}
            eventDidMount={(info) => {
              //Place the popover
              const popover: HTMLElement | null =
                document.querySelector(".fc-popover");
              if (popover && tableHeight) {
                // Find the row number of the popover cell:
                let cellRowNumber = 0;
                const cellDateString = popover
                  ? popover.getAttribute("data-date")
                  : null;
                if (!cellDateString) return;
                const rows = document.querySelectorAll('tr[role="row"]');
                const rowsArray = Array.from(rows);
                rowsArray?.map((row, index) => {
                  const rowDatesArray: string[] = [];
                  (row.childNodes as NodeListOf<HTMLElement>).forEach(
                    (element: HTMLElement) =>
                      element.dataset.date &&
                      rowDatesArray.push(element.dataset.date),
                  );
                  if (rowDatesArray.includes(cellDateString))
                    cellRowNumber = index;
                });
                // Place the popover considering the overflow option (the timeout is for getting the correct popover height size):
                setTimeout(() => {
                  const popoverHeight = popover.getBoundingClientRect().height;
                  const rowHeight = tableHeight / 5;
                  if (
                    popoverHeight + rowHeight >
                    (5 - cellRowNumber) * rowHeight
                  ) {
                    const popoverOverflowAmount =
                      popoverHeight - (5 - cellRowNumber) * rowHeight;
                    popover.style.marginTop = `${rowHeight - popoverOverflowAmount}px`;
                  } else popover.style.marginTop = `${tableHeight / 5}px`;
                }, 10);
              }

              // Give the events a different style when theres less than 3 events in a cell:
              const processedCells = new Set<string>();
              const cell = info.el.closest(
                ".fc-daygrid-day",
              ) as HTMLElement | null;
              const cellDate = cell?.getAttribute("data-date");
              if (cell && cellDate && !processedCells.has(cellDate)) {
                processedCells.add(cellDate);
                const events = cell.querySelectorAll(".fc-event");
                if (tableHeight && events.length < 3) {
                  const dateNumberHeight = 22.391;
                  const modifiedEventHeight =
                    (tableHeight / 5 - dateNumberHeight) / 2 - 5;
                  events.forEach((event) => {
                    const eventElement = event as HTMLElement;
                    eventElement.style.height = `${modifiedEventHeight}px`;
                    eventElement.style.whiteSpace = `normal`;
                    eventElement.style.setProperty("display", "-webkit-box");
                    eventElement.style.setProperty(
                      "-webkit-box-orient",
                      "vertical",
                    );
                    eventElement.style.setProperty("-webkit-line-clamp", "2");
                    eventElement.style.setProperty("align-content", "center");
                  });
                }
              }
            }}
          />
        </div>
      )}
      <GroupCard
        isEdit={!!group}
        isOpen={isAddGroup || !!group}
        onEditComplete={() => {
          void refetch();
        }}
        onSaveComplete={() => {
          void refetch();
        }}
        onClose={() => {
          setIsAddGroup(false);
          setGroupId("");
          setGroup(undefined);
        }}
        groupDetails={group}
      />
    </div>
  );
};
